import { useInView } from '../../../hooks/useInView';

const reasons = [
  {
    icon: 'ri-broadcast-fill',
    title: 'Broadcast-Grade Quality',
    desc: 'We operate at the same production standards as major television networks and Fortune 500 commercial campaigns — the level audiences associate with the world\'s most authoritative brands.',
  },
  {
    icon: 'ri-compass-3-line',
    title: 'Strategy-First Storytelling',
    desc: 'Every creative decision is guided by your business goals. We don\'t just make it look good — we architect content that works harder, converts better, and builds lasting brand authority.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Professional-Grade Equipment',
    desc: 'Cinema cameras, broadcast lighting systems, drone units, and professional audio — the full arsenal of tools required for premium broadcast-ready production outcomes.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Flawless On-Time Delivery',
    desc: 'Twenty years of on-schedule delivery. You\'ll always know exactly where your project stands — transparent communication, no surprises, no delays, and no compromises.',
  },
  {
    icon: 'ri-global-line',
    title: 'Bilingual Production Team',
    desc: 'Full production capabilities in English and Spanish — enabling seamless communication and culturally resonant storytelling for brands targeting diverse markets across Florida and beyond.',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Results-Focused Creative',
    desc: 'We measure success by the impact your content creates — audience engagement rates, brand authority signals, and the conversion performance your investment was designed to achieve.',
  },
];

export default function WhyChooseUs() {
  const { ref, inView } = useInView();

  return (
    <section className="relative overflow-hidden" id="why-us">
      {/* Full-bleed split background */}
      <div className="absolute inset-0 grid grid-cols-2">
        <div className="bg-[#070707]" />
        <div className="relative overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/a1fc75c9b76d6f3cb0c3855f667f6a2d.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070707] via-[#070707]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707]/60 to-transparent" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-20 py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* Left — sticky panel */}
          <div
            ref={ref as React.RefObject<HTMLDivElement>}
            className={`lg:col-span-5 lg:sticky lg:top-36 transition-all duration-800 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Why Choose Us</p>
            <h2 className="font-serif font-bold text-white leading-[0.95] mb-8" style={{ fontSize: 'clamp(2.8rem, 4.5vw, 4.8rem)', letterSpacing: '-0.025em' }}>
              Why Leading<br />Brands Choose<br />
              <em className="not-italic text-gold">Immanuel</em>
            </h2>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-14 h-px bg-gold" />
              <div className="w-4 h-px bg-gold/40" />
            </div>
            <p className="font-sans text-sm text-white/40 leading-[1.95] max-w-sm mb-12">
              For over two decades, we've been the production partner of choice for brands, broadcasters, government organizations, and creative teams who refuse to settle for anything less than broadcast excellence.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6 mb-12">
              {[['20+','Years Experience'],['500+','Productions'],['2','Languages'],['100%','Satisfaction']].map(([n,l]) => (
                <div key={l} className="group cursor-default">
                  <div className="flex items-end gap-2 mb-1">
                    <p className="font-serif text-4xl font-bold text-gold leading-none">{n}</p>
                  </div>
                  <p className="font-sans text-[0.57rem] text-white/35 tracking-[0.2em] uppercase">{l}</p>
                  <div className="w-8 h-px bg-gold/20 group-hover:bg-gold/50 transition-colors duration-300 mt-2" />
                </div>
              ))}
            </div>

            {/* Credibility image */}
            <div className="relative h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20video%20production%20award%20winning%20crew%20dark%20studio%20with%20cinema%20cameras%20expensive%20broadcast%20equipment%20professional%20lighting%20rigs%20behind%20the%20scenes%20media%20production%20team%20authentic%20cinematic%20atmosphere%20dark%20moody&width=600&height=380&seq=why3_cred&orientation=landscape"
                alt="Professional production excellence"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070707]/60 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="font-sans text-[0.58rem] tracking-[0.22em] uppercase text-gold/80 bg-[#070707]/85 px-3 py-1.5 backdrop-blur-sm border border-gold/15">
                  Est. 2005 &nbsp;&bull;&nbsp; Central Florida
                </span>
              </div>
            </div>
          </div>

          {/* Right — reasons */}
          <div
            className={`lg:col-span-7 transition-all duration-800 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <div className="flex flex-col gap-3">
              {reasons.map((r, i) => (
                <div
                  key={r.title}
                  className="group flex items-start gap-6 p-7 lg:p-8 border border-white/5 hover:border-gold/25 bg-white/[0.012] hover:bg-white/[0.03] transition-all duration-400 cursor-default"
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-gold/20 group-hover:border-gold/55 bg-gold/5 flex-shrink-0 transition-all duration-300">
                    <i className={`${r.icon} text-gold text-lg`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-sans text-sm font-semibold text-white mb-2 tracking-wide">{r.title}</h3>
                    <p className="font-sans text-[0.77rem] text-white/38 leading-relaxed group-hover:text-white/60 transition-colors duration-300">{r.desc}</p>
                  </div>
                  <i className="ri-arrow-right-line text-gold/0 group-hover:text-gold/50 transition-all duration-300 flex-shrink-0 self-center ml-2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
